import { c as _c } from "react/compiler-runtime";
import { IcoTrash16 } from '@onefootprint/icons';
import type { CustomRequirementCondition, CustomRequirementConditionExpression, DataIdentifier } from '@onefootprint/request-types/dashboard';
import { Button, IconButton, Select, TextInput, Toggle } from '@onefootprint/ui';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { CustomQuestionsFormData, Question } from '../types';
type SettingsLogicProps = {
  questionIndex: number;
  availableQuestions: Exclude<Question, {
    kind: 'text_block';
  }>[];
  canEdit: boolean;
};
export type SettingsLogicRef = {
  hasIncompleteRules: () => boolean;
  clearLogic: () => void;
  enableLogic: () => void;
};
type LogicRule = {
  field: string;
  op: 'eq' | 'not_eq' | 'exists';
  value?: string | number | boolean;
};
const kindLabelKeys = {
  short_text: 'shortText',
  long_text: 'longText',
  number: 'number',
  dropdown: 'dropdown',
  checkboxes: 'multiSelect',
  checkbox: 'checkbox',
  multiple_choice: 'multipleChoice',
  address: 'address'
} as const;
const SettingsLogic = forwardRef<SettingsLogicRef, SettingsLogicProps>((t0, ref) => {
  const $0 = _c(72);
  const {
    questionIndex,
    availableQuestions,
    canEdit
  } = t0;
  const {
    t
  } = useTranslation("playbook-custom-questions");
  const {
    control,
    setValue
  } = useFormContext();
  const t1 = `questions.${questionIndex}`;
  let t2;
  if ($0[0] !== control || $0[1] !== t1) {
    t2 = {
      control,
      name: t1
    };
    $0[0] = control;
    $0[1] = t1;
    $0[2] = t2;
  } else {
    t2 = $0[2];
  }
  const currentQuestion = useWatch(t2);
  const [enabled, setEnabled] = useState(!!currentQuestion?.condition);
  const [operator, setOperator] = useState("and");
  let t3;
  if ($0[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $0[3] = t3;
  } else {
    t3 = $0[3];
  }
  const [rules, setRules] = useState(t3);
  let t4;
  if ($0[4] !== availableQuestions || $0[5] !== t) {
    t4 = question => {
      if (question.label) {
        return question.label;
      }
      const kind = t($ => $.addContent[kindLabelKeys[question.kind]]);
      const sameKind = availableQuestions.filter(q => !q.label && q.kind === question.kind);
      return sameKind.length > 1 ? t(_temp, {
        kind,
        index: sameKind.indexOf(question) + 1
      }) : t(_temp2, {
        kind
      });
    };
    $0[4] = availableQuestions;
    $0[5] = t;
    $0[6] = t4;
  } else {
    t4 = $0[6];
  }
  const questionLabel = t4;
  let t5;
  if ($0[7] !== currentQuestion) {
    t5 = () => {
      if (currentQuestion?.condition) {
        const condition = currentQuestion.condition;
        const newRules = [];
        let detectedOperator = "and";
        const processCondition = cond => {
          if (cond.kind === "expression") {
            if ("value" in cond && (cond.op === "eq" || cond.op === "not_eq")) {
              newRules.push({
                field: cond.field,
                op: cond.op,
                value: cond.value as string | number | boolean
              });
            } else {
              if (cond.op === "exists") {
                newRules.push({
                  field: cond.field,
                  op: cond.op
                });
              }
            }
          } else {
            if (cond.kind === "and") {
              detectedOperator = "and";
              cond.conditions.forEach(processCondition);
            } else {
              if (cond.kind === "or") {
                detectedOperator = "or";
                cond.conditions.forEach(processCondition);
              }
            }
          }
        };
        processCondition(condition);
        setRules(newRules);
        setOperator(detectedOperator);
        setEnabled(true);
      } else {
        setRules([]);
        setOperator("and");
        setEnabled(false);
      }
    };
    $0[7] = currentQuestion;
    $0[8] = t5;
  } else {
    t5 = $0[8];
  }
  const t6 = currentQuestion?.condition;
  let t7;
  if ($0[9] !== t6) {
    t7 = [t6];
    $0[9] = t6;
    $0[10] = t7;
  } else {
    t7 = $0[10];
  }
  useEffect(t5, t7);
  let t8;
  if ($0[11] !== operator || $0[12] !== questionIndex || $0[13] !== setValue) {
    t8 = (newRules_0, isEnabled, t9) => {
      const currentOperator = t9 === undefined ? operator : t9;
      if (!isEnabled) {
        setValue(`questions.${questionIndex}.condition`, undefined, {
          shouldDirty: true
        });
        return;
      }
      if (newRules_0.length === 0) {
        setValue(`questions.${questionIndex}.condition`, undefined, {
          shouldDirty: true
        });
        return;
      }
      const expressions = newRules_0.map(_temp3);
      let condition_0;
      if (expressions.length === 1) {
        condition_0 = expressions[0];
      } else {
        condition_0 = {
          kind: currentOperator,
          conditions: expressions
        };
      }
      setValue(`questions.${questionIndex}.condition`, condition_0, {
        shouldDirty: true
      });
    };
    $0[11] = operator;
    $0[12] = questionIndex;
    $0[13] = setValue;
    $0[14] = t8;
  } else {
    t8 = $0[14];
  }
  const updateCondition = t8;
  let t9;
  if ($0[15] !== availableQuestions[0] || $0[16] !== rules || $0[17] !== updateCondition) {
    t9 = checked => {
      setEnabled(checked);
      if (checked && rules.length === 0) {
        const defaultQuestion = availableQuestions[0];
        if (defaultQuestion) {
          const newRules_1 = [{
            field: defaultQuestion.id,
            op: "eq",
            value: ""
          }];
          setRules(newRules_1);
          updateCondition(newRules_1, true);
        }
      } else {
        updateCondition(rules, checked);
      }
    };
    $0[15] = availableQuestions[0];
    $0[16] = rules;
    $0[17] = updateCondition;
    $0[18] = t9;
  } else {
    t9 = $0[18];
  }
  const handleToggle = t9;
  let t10;
  if ($0[19] !== availableQuestions[0] || $0[20] !== rules || $0[21] !== updateCondition) {
    t10 = () => {
      const defaultQuestion_0 = availableQuestions[0];
      if (!defaultQuestion_0) {
        return;
      }
      const newRules_2 = [...rules, {
        field: defaultQuestion_0.id,
        op: "eq",
        value: ""
      }];
      setRules(newRules_2);
      updateCondition(newRules_2, true);
    };
    $0[19] = availableQuestions[0];
    $0[20] = rules;
    $0[21] = updateCondition;
    $0[22] = t10;
  } else {
    t10 = $0[22];
  }
  const handleAddRule = t10;
  let t11;
  if ($0[23] !== rules || $0[24] !== updateCondition) {
    t11 = index => {
      const newRules_3 = rules.filter((_, i) => i !== index);
      setRules(newRules_3);
      updateCondition(newRules_3, true);
      if (newRules_3.length === 0) {
        setEnabled(false);
        updateCondition([], false);
      }
    };
    $0[23] = rules;
    $0[24] = updateCondition;
    $0[25] = t11;
  } else {
    t11 = $0[25];
  }
  const handleRemoveRule = t11;
  let t12;
  if ($0[26] !== rules || $0[27] !== updateCondition) {
    t12 = newOperator => {
      setOperator(newOperator);
      updateCondition(rules, true, newOperator);
    };
    $0[26] = rules;
    $0[27] = updateCondition;
    $0[28] = t12;
  } else {
    t12 = $0[28];
  }
  const handleOperatorChange = t12;
  let t13;
  if ($0[29] !== availableQuestions || $0[30] !== rules || $0[31] !== updateCondition) {
    t13 = (index_0, updates) => {
      const newRules_4 = rules.map((rule_0, i_0) => i_0 === index_0 ? {
        ...rule_0,
        ...updates
      } : rule_0);
      if (updates.field) {
        const targetQuestion = availableQuestions.find(q_0 => q_0.id === updates.field);
        if (targetQuestion) {
          if (newRules_4[index_0].op !== "exists") {
            newRules_4[index_0].value = "";
          } else {
            delete newRules_4[index_0].value;
          }
        }
      }
      if (updates.op === "exists") {
        delete newRules_4[index_0].value;
      } else {
        if ((updates.op === "eq" || updates.op === "not_eq") && newRules_4[index_0].value === undefined) {
          newRules_4[index_0].value = "";
        }
      }
      setRules(newRules_4);
      updateCondition(newRules_4, true);
    };
    $0[29] = availableQuestions;
    $0[30] = rules;
    $0[31] = updateCondition;
    $0[32] = t13;
  } else {
    t13 = $0[32];
  }
  const handleUpdateRule = t13;
  let t14;
  if ($0[33] !== enabled || $0[34] !== rules) {
    t14 = () => enabled && rules.some(_temp4);
    $0[33] = enabled;
    $0[34] = rules;
    $0[35] = t14;
  } else {
    t14 = $0[35];
  }
  const hasIncompleteRules = t14;
  let t15;
  if ($0[36] !== updateCondition) {
    t15 = () => {
      setEnabled(false);
      setRules([]);
      updateCondition([], false);
    };
    $0[36] = updateCondition;
    $0[37] = t15;
  } else {
    t15 = $0[37];
  }
  const clearLogic = t15;
  let t16;
  if ($0[38] !== enabled || $0[39] !== handleToggle) {
    t16 = () => {
      if (!enabled) {
        handleToggle(true);
      }
    };
    $0[38] = enabled;
    $0[39] = handleToggle;
    $0[40] = t16;
  } else {
    t16 = $0[40];
  }
  const enableLogic = t16;
  let t17;
  if ($0[41] !== clearLogic || $0[42] !== enableLogic || $0[43] !== hasIncompleteRules) {
    t17 = () => ({
      hasIncompleteRules,
      clearLogic,
      enableLogic
    });
    $0[41] = clearLogic;
    $0[42] = enableLogic;
    $0[43] = hasIncompleteRules;
    $0[44] = t17;
  } else {
    t17 = $0[44];
  }
  useImperativeHandle(ref, t17);
  let t18;
  if ($0[45] !== availableQuestions || $0[46] !== handleUpdateRule || $0[47] !== t) {
    t18 = (rule_2, index_1) => {
      if (rule_2.op === "exists") {
        return null;
      }
      const targetQuestion_0 = availableQuestions.find(q_1 => q_1.id === rule_2.field);
      if (!targetQuestion_0) {
        return null;
      }
      if (targetQuestion_0.kind === "multiple_choice" || targetQuestion_0.kind === "dropdown" || targetQuestion_0.kind === "checkboxes") {
        const options = targetQuestion_0.options.map(_temp5);
        return <Select value={rule_2.value as string} onChange={val => handleUpdateRule(index_1, {
          value: val
        })} options={options} size="compact" />;
      }
      if (targetQuestion_0.kind === "checkbox") {
        return <Select value={String(rule_2.value)} onChange={val_0 => handleUpdateRule(index_1, {
          value: val_0 === "true"
        })} options={[{
          label: t(_temp6),
          value: "true"
        }, {
          label: t(_temp7),
          value: "false"
        }]} size="compact" />;
      }
      if (targetQuestion_0.kind === "number") {
        return <TextInput type="number" value={String(rule_2.value)} onChange={e => handleUpdateRule(index_1, {
          value: Number(e.target.value) || 0
        })} size="compact" />;
      }
      return <TextInput value={rule_2.value as string} onChange={e_0 => handleUpdateRule(index_1, {
        value: e_0.target.value
      })} size="compact" />;
    };
    $0[45] = availableQuestions;
    $0[46] = handleUpdateRule;
    $0[47] = t;
    $0[48] = t18;
  } else {
    t18 = $0[48];
  }
  const renderValueInput = t18;
  const t19 = !canEdit || availableQuestions.length === 0;
  let t20;
  if ($0[49] !== t) {
    t20 = t(_temp8);
    $0[49] = t;
    $0[50] = t20;
  } else {
    t20 = $0[50];
  }
  let t21;
  if ($0[51] !== enabled || $0[52] !== handleToggle || $0[53] !== t19 || $0[54] !== t20) {
    t21 = <Toggle checked={enabled} onChange={handleToggle} disabled={t19} label={t20} size="compact" labelPlacement="left" fullWidth={true} />;
    $0[51] = enabled;
    $0[52] = handleToggle;
    $0[53] = t19;
    $0[54] = t20;
    $0[55] = t21;
  } else {
    t21 = $0[55];
  }
  let t22;
  if ($0[56] !== availableQuestions || $0[57] !== canEdit || $0[58] !== enabled || $0[59] !== handleAddRule || $0[60] !== handleOperatorChange || $0[61] !== handleRemoveRule || $0[62] !== handleUpdateRule || $0[63] !== operator || $0[64] !== questionLabel || $0[65] !== renderValueInput || $0[66] !== rules || $0[67] !== t) {
    t22 = enabled && <fieldset disabled={!canEdit} className="contents"><hr className="border-t border-tertiary" /><div className="flex flex-col gap-4"><p className="text-sm font-medium text-primary">{t(_temp9)}</p><div className="flex flex-col gap-4">{rules.map((rule_3, ruleIndex) => <div key={`${rule_3.field}-${ruleIndex}`} className="flex flex-col gap-3">{ruleIndex > 0 && <div className="w-[81px]"><Select value={operator} onChange={val_1 => handleOperatorChange(val_1 as 'and' | 'or')} options={[{
                label: t(_temp10),
                value: "and"
              }, {
                label: t(_temp11),
                value: "or"
              }]} size="compact" disabled={!canEdit} /></div>}<div className="flex flex-col gap-1 relative group"><div className="flex gap-1 items-center"><div className="flex-1 min-w-0"><Select value={rule_3.field} onChange={val_2 => handleUpdateRule(ruleIndex, {
                    field: val_2
                  })} options={availableQuestions.map(q_2 => ({
                    label: questionLabel(q_2),
                    value: q_2.id
                  }))} size="compact" placeholder={t(_temp12)} disabled={!canEdit} /></div>{canEdit && <IconButton icon={IcoTrash16} size="tiny" variant="ghost" destructive={true} onClick={() => handleRemoveRule(ruleIndex)} aria-label={t(_temp13)} />}</div><Select value={rule_3.op} onChange={val_3 => handleUpdateRule(ruleIndex, {
                op: val_3 as 'eq' | 'not_eq' | 'exists'
              })} options={[{
                label: t(_temp14),
                value: "eq"
              }, {
                label: t(_temp15),
                value: "not_eq"
              }, {
                label: t(_temp16),
                value: "exists"
              }]} size="compact" disabled={!canEdit} />{renderValueInput(rule_3, ruleIndex)}</div></div>)}{canEdit && <Button variant="secondary" size="compact" onClick={handleAddRule}>{t(_temp17)}</Button>}</div></div></fieldset>;
    $0[56] = availableQuestions;
    $0[57] = canEdit;
    $0[58] = enabled;
    $0[59] = handleAddRule;
    $0[60] = handleOperatorChange;
    $0[61] = handleRemoveRule;
    $0[62] = handleUpdateRule;
    $0[63] = operator;
    $0[64] = questionLabel;
    $0[65] = renderValueInput;
    $0[66] = rules;
    $0[67] = t;
    $0[68] = t22;
  } else {
    t22 = $0[68];
  }
  let t23;
  if ($0[69] !== t21 || $0[70] !== t22) {
    t23 = <div className="flex flex-col gap-4">{t21}{t22}</div>;
    $0[69] = t21;
    $0[70] = t22;
    $0[71] = t23;
  } else {
    t23 = $0[71];
  }
  return t23;
});
SettingsLogic.displayName = 'SettingsLogic';
export default SettingsLogic;
function _temp($_0) {
  return $_0.logic.untitledNumbered;
}
function _temp2($_1) {
  return $_1.logic.untitled;
}
function _temp3(rule) {
  switch (rule.op) {
    case "exists":
      {
        return {
          kind: "expression",
          field: rule.field as DataIdentifier,
          op: "exists"
        };
      }
    case "eq":
      {
        return {
          kind: "expression",
          field: rule.field as DataIdentifier,
          op: "eq",
          value: rule.value ?? ""
        };
      }
    case "not_eq":
      {
        return {
          kind: "expression",
          field: rule.field as DataIdentifier,
          op: "not_eq",
          value: rule.value ?? ""
        };
      }
    default:
      {
        const _exhaustiveCheck = rule.op;
        throw new Error(`Unsupported logic operator: ${_exhaustiveCheck}`);
      }
  }
}
function _temp4(rule_1) {
  if (typeof rule_1.value === "string" && rule_1.value.trim() === "") {
    return true;
  }
  if (rule_1.op === "exists") {
    return false;
  }
  if (rule_1.value === undefined || rule_1.value === null) {
    return true;
  }
  return false;
}
function _temp5(opt) {
  return {
    label: opt.label,
    value: opt.label
  };
}
function _temp6($_2) {
  return $_2.logic.checked;
}
function _temp7($_3) {
  return $_3.logic.unchecked;
}
function _temp8($_4) {
  return $_4.logic.enableConditionalLogic;
}
function _temp9($_5) {
  return $_5.logic.showWhen;
}
function _temp10($_6) {
  return $_6.logic.and;
}
function _temp11($_7) {
  return $_7.logic.or;
}
function _temp12($_8) {
  return $_8.logic.selectQuestion;
}
function _temp13($_9) {
  return $_9.logic.removeCondition;
}
function _temp14($_10) {
  return $_10.logic.equals;
}
function _temp15($_11) {
  return $_11.logic.notEquals;
}
function _temp16($_12) {
  return $_12.logic.exists;
}
function _temp17($_13) {
  return $_13.logic.addCondition;
}